import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

// Client-side mirror of backend MODULES catalog (keep in sync with server.py MODULES)
const MODULE_ROLES = {
  finance:         ["super_admin","owner","admin","director","accountant","cashier","secretary"],
  hr:              ["super_admin","owner","admin","director","hr"],
  academic:        ["super_admin","owner","admin","director","study_director","secretary"],
  students_write:  ["super_admin","owner","admin","director","study_director","secretary"],
  library:         ["super_admin","owner","admin","director","librarian"],
  admissions:      ["super_admin","owner","admin","director","secretary"],
  audit:           ["super_admin","owner","admin","director","study_director","auditor"],
  admin_panel:     ["super_admin"],
  reports:         ["super_admin","owner","admin","director","study_director"],
  messaging:       ["*"],
  timetable:       ["*"],
  user_management: ["super_admin","owner","admin","director","hr"],
};

export function hasModuleAccess(user, module) {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  const roles = MODULE_ROLES[module];
  if (!roles) return false;
  if (roles.includes("*")) return true;
  if (roles.includes(user.role)) return true;
  return (user.permissions || []).includes(module);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=user
  const [school, setSchool] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.school_id) {
        const sch = await api.get("/schools/current").then((r) => r.data).catch(() => null);
        setSchool(sch);
      } else {
        setSchool(null);
      }
    } catch {
      setUser(false);
      setSchool(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply school primary_color dynamically as CSS variables (HSL)
  useEffect(() => {
    const hex = school?.primary_color;
    if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
      document.documentElement.style.removeProperty("--accent");
      document.documentElement.style.removeProperty("--ring");
      return;
    }
    // hex → HSL
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    const hsl = `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    document.documentElement.style.setProperty("--accent", hsl);
    document.documentElement.style.setProperty("--ring", hsl);
  }, [school?.primary_color]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.token) localStorage.setItem("access_token", data.token);
    setUser(data.user);
    if (data.user?.school_id) {
      const sch = await api.get("/schools/current").then((r) => r.data).catch(() => null);
      setSchool(sch);
    }
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("access_token");
    setUser(false);
    setSchool(null);
  };

  return (
    <AuthContext.Provider value={{ user, school, setSchool, refresh, login, logout, formatApiError,
                                   hasModule: (m) => hasModuleAccess(user, m) }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
